import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import Layout from './components/Layout';
import Login from './pages/Login';
import {
  RouteErrorBoundaryWithLocation,
  RouteLoadSuccess,
} from './components/RouteErrorBoundary';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Characters = lazy(() => import('./pages/Characters'));
const Studio = lazy(() => import('./pages/Studio'));
const Media = lazy(() => import('./pages/Media'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-200">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent-violet"></div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <RouteFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RouteLoadSuccess>
            <Login />
          </RouteLoadSuccess>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <RouteErrorBoundaryWithLocation>
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <RouteLoadSuccess>
                          <Dashboard />
                        </RouteLoadSuccess>
                      }
                    />
                    <Route
                      path="/characters"
                      element={
                        <RouteLoadSuccess>
                          <Characters />
                        </RouteLoadSuccess>
                      }
                    />
                    <Route
                      path="/studio/*"
                      element={<Studio />}
                    />
                    <Route
                      path="/media"
                      element={
                        <RouteLoadSuccess>
                          <Media />
                        </RouteLoadSuccess>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <RouteLoadSuccess>
                          <Reports />
                        </RouteLoadSuccess>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <RouteLoadSuccess>
                          <Settings />
                        </RouteLoadSuccess>
                      }
                    />
                  </Routes>
                </Suspense>
              </RouteErrorBoundaryWithLocation>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
